// Kontraktet för /statistics/v2/{interval} och utvärderingen efter en aktivitet
// (backendens StatisticsOverview och ActivityEvaluation, V012). Allt räknas i backend;
// vyerna visar och räknar ingenting själva. Ett avsnitt som mottagaren inte får se är null,
// och visibility säger vilka.

export type EvaluationStatus = 'answered' | 'declined' | 'legacy_unknown';
export type EvaluationEnergy = 'giving' | 'neutral' | 'draining';
export type EvaluationTag =
  | 'hard_to_start'
  | 'took_longer'
  | 'interrupted'
  | 'tired'
  | 'fun'
  | 'easy_to_start'
  | 'got_help'
  | 'finished_on_time';
export type EvaluatedBy = 'self' | 'supervisor';

/** Alla taggar, i den ordning statistiken listar dem. */
export const EVALUATION_TAGS: readonly EvaluationTag[] = [
  'fun',
  'easy_to_start',
  'got_help',
  'finished_on_time',
  'hard_to_start',
  'took_longer',
  'interrupted',
  'tired',
];

const POSITIVE_TAGS: readonly EvaluationTag[] = [
  'fun',
  'easy_to_start',
  'got_help',
  'finished_on_time',
];
const NEGATIVE_TAGS: readonly EvaluationTag[] = [
  'hard_to_start',
  'took_longer',
  'interrupted',
  'tired',
];
const MIXED_TAGS: readonly EvaluationTag[] = ['got_help', 'took_longer', 'fun', 'tired'];

/**
 * De taggar som passar svaret. Energin avgör när den är besvarad, annars känslan: en glad
 * gubbe får inte "svårt att komma igång" som förslag. Glad men "tog energi" (roligt men
 * tröttande, vanligt vid NPF) får de negativa taggarna plus "Var roligt".
 */
export const tagsForAnswer = (
  mood: number | null,
  energy: EvaluationEnergy | null,
): readonly EvaluationTag[] => {
  if (energy === 'giving') return POSITIVE_TAGS;
  if (energy === 'draining')
    return mood !== null && mood >= 4 ? [...NEGATIVE_TAGS, 'fun'] : NEGATIVE_TAGS;
  if (energy === 'neutral') return MIXED_TAGS;
  if (mood !== null && mood >= 4) return POSITIVE_TAGS;
  if (mood !== null && mood <= 2) return NEGATIVE_TAGS;
  return MIXED_TAGS;
};

/** Samma gräns som kolumnen activity_evaluations.note. */
export const EVALUATION_NOTE_MAX = 150;

export type ActivityEvaluation = {
  activityInstanceId: number;
  status: EvaluationStatus;
  mood: number | null;
  energy: EvaluationEnergy | null;
  tags: EvaluationTag[];
  /** Brukarens egna ord. Privat: alltid null när en handledare frågar. */
  note: string | null;
  evaluatedBy: EvaluatedBy | null;
  /** baseApi:s parseDates gör tidsstämplar till Date. */
  evaluatedAt: Date | string | null;
};

/** Det som skickas in. legacy_unknown finns bara i historiken och kan inte skickas. */
export type ActivityEvaluationInput = {
  status: 'answered' | 'declined';
  mood?: number | null;
  energy?: EvaluationEnergy | null;
  tags?: EvaluationTag[];
  note?: string | null;
};

export type StatisticsInterval = 'day' | 'week' | 'month' | 'year';

export type Weekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export type TimeOfDaySlot = 'morning' | 'midday' | 'afternoon' | 'evening';

export type EnergyCounts = { giving: number; neutral: number; draining: number };

export type StatisticsVisibility = {
  completion: boolean;
  evaluation: boolean;
  activityNames: boolean;
  tags: boolean;
  aiSummary: boolean;
};

export type StatisticsCompletion = {
  planned: number;
  completed: number;
  startedNotCompleted: number;
  notStarted: number;
  completionRate: number | null;
};

export type StatisticsEvaluation = {
  answered: number;
  declined: number;
  notAnswered: number;
  legacyUnknown: number;
  responseRate: number | null;
  moodDistribution: Record<string, number>;
  moodMedian: number | null;
  energy: EnergyCounts;
  evaluatedBySupervisor: number;
  tags: Record<EvaluationTag, number> | null;
};

export type DayPattern = {
  date: string;
  weekday: Weekday;
  planned: number;
  completed: number;
  notStarted: number;
  answered: number;
  moodMedian: number | null;
  energy: EnergyCounts | null;
};

export type WeekdayPattern = {
  weekday: Weekday;
  planned: number;
  completed: number;
  notStarted: number;
  answered: number;
  moodMedian: number | null;
};

export type TimeOfDayPattern = {
  slot: TimeOfDaySlot;
  planned: number;
  completed: number;
  notStarted: number;
};

export type ActivityPattern = {
  activityId: number;
  name: string;
  recurring: boolean;
  planned: number;
  completed: number;
  notStarted: number;
  completionRate: number | null;
  answered: number;
  moodMedian: number | null;
  energy: EnergyCounts | null;
  givingSignals: number;
  drainingSignals: number;
};

export type StatisticsOverview = {
  period: { type: StatisticsInterval; start: string; end: string };
  audience: 'self' | 'supervisor';
  persona: 'child' | 'teen' | 'adult';
  visibility: StatisticsVisibility;
  completion: StatisticsCompletion | null;
  evaluation: StatisticsEvaluation | null;
  comparison: {
    completion: StatisticsCompletion;
    evaluation: StatisticsEvaluation | null;
  } | null;
  patterns: {
    byDay: DayPattern[] | null;
    byWeekday: WeekdayPattern[];
    byTimeOfDay: TimeOfDayPattern[];
    byActivity: ActivityPattern[] | null;
  } | null;
  highlights: {
    givesEnergy: ActivityPattern[] | null;
    takesEnergy: ActivityPattern[] | null;
    oftenNotStarted: ActivityPattern[];
  } | null;
  sufficiency: { minN: number; enough: boolean };
};
